import {useEffect} from "react";
import {useOverlayActivity} from "components/useOverlayActivity";
import {useLocale, useMessages} from "i18n";
import formatBytes from "core/format/byteFormat";
import {SubscriptionElementsCheckout} from "pay/SubscriptionElementsCheckout";
import {ImmediateDeliveryConsent} from "pay/ImmediateDeliveryConsent";
import {paymentAdmission, withdrawalEffectOf} from "pay/withdrawalRight";
import {useUser} from "UserProvider";
import {formatMoney} from "pricing/PricingStorage";
import {SheetTransition} from "transitions/SheetTransition";
import {CloseButton, Continue, Lede, OfferUnavailable, Title} from "upsell/ExitOfferDialog.styles";
import {couponDiscountsLeft, type ClaimQuote, type CurrentLadderGift} from "./currentLadderOffer";
import {closeExpiredCouponDialog, type CouponCountdown} from "./couponCountdown";
import {CouponTicket} from "./CouponTicket";
import {useCouponClaim} from "./useCouponClaim";
import {upgradeChargeLine} from "./upgradeChargeLine";
import {useCheckoutDocument} from "pay/useCheckoutDocument";
import {OrderTermsLine} from "pay/OrderTermsLine";
import {ClaimBenefit, ClaimContent, ClaimDialog, ClaimEyebrow, ClaimLayout, ClaimTerms, TicketColumn} from "./CouponClaimDialog.styles";

/**
 * A single target plan, sharing the exit offer's dialog and controls, never the full pricing picker.
 *
 * Subscribe pays IN THE DIALOG (owner, 2026-09-09): the minted embedded session's sheet replaces the
 * plan copy in the content column, beside the same ticket. While the sheet is up a scrim click must
 * not close the dialog — a card number lives in the cross-origin Stripe frame and a stray click would
 * throw it away — the same rule the first-month claim dialog states; the ✕ stays.
 */
export function CouponClaimDialog({gift, countdown, onClose, onExpired}: {
	gift: CurrentLadderGift; countdown: CouponCountdown; onClose: () => void; onExpired: () => void;
}) {
	const claim = useCouponClaim(gift, onClose, onExpired, countdown.expired);
	useEffect(() => {
		if (closeExpiredCouponDialog(countdown, claim.busy, claim.clientSecret)) onClose();
	}, [countdown, claim.busy, claim.clientSecret, onClose]);
	return <CouponClaimDialogView gift={gift} countdown={countdown} discountsLeft={couponDiscountsLeft(gift)}
		claim={claim} onClose={onClose}/>;
}

export type CouponClaimActions = ReturnType<typeof useCouponClaim>;

/**
 * The same ticket and inline checkout, independent of how this offer was authorized.
 *
 * <b>A {@link ClaimQuote} with `discount: null` is the plan at its REGULAR price</b> (the limit-wall
 * email once the day's coupons are spent, kitecorp/transferify-api-service#2206): no ticket, no coupon
 * eyebrow and no first-month line, because there is no coupon to show, and the one-column layout keeps an
 * empty ticket panel off the dialog. Everything that pays (consent, Subscribe, the inline sheet) is the
 * same, so the two states cannot drift apart. `note` is the sentence saying why the price is the regular one.
 *
 * <b>A gift with `intro` is the Halloween ticket</b> (kitecorp/transferify-ui#3776): its eyebrow, its dialog's name
 * and its one price line name the ticket and its 30 days, and that same sentence is the order block's intro
 * line, so the print, the dialog and the Stripe sheet state one set of figures.
 */
export function CouponClaimDialogView({gift, countdown, discountsLeft, claim, onClose, open = true, onTransitionExited, eyebrow, note, endsOn}: {
	gift: ClaimQuote; countdown?: CouponCountdown; discountsLeft?: number;
	claim: CouponClaimActions; onClose: () => void; open?: boolean; onTransitionExited?: () => void;
	/** Overrides the generic "Special coupon" line — the promo dialog names its platform here. */
	eyebrow?: string;
	note?: string;
	/** The weekday the offer ends, on the ticket's expiry line, for an entry with a deadline but no countdown. */
	endsOn?: string;
}) {
	const {offerLadder, pricing, common, checkout: checkoutCopy, errors} = useMessages();
	const {locale} = useLocale();
	const contract = useCheckoutDocument(open);
	const [user] = useUser();
	const overlayExited = useOverlayActivity(open);
	const onExited = () => { overlayExited(); onTransitionExited?.(); };
	const {subscribe, acknowledged, setAcknowledged, busy, expired, previewStatus, amountDueNow, clientSecret,
		collectsShippingAddress, checkoutError, paid, dismissSheet} = claim;
	const sheetUp = clientSecret !== null;
	const coupon = gift.discount === null ? null : gift;
	const renewal = formatMoney(gift.currency, gift.renewal, locale);
	const priceLine = coupon === null ? offerLadder.limitWall.regularPrice(renewal)
		: coupon.intro === undefined ? offerLadder.gift.price(formatMoney(coupon.currency, coupon.firstMonth, locale), renewal)
		: offerLadder.gift.introTitle(coupon.intro.days, pricing.planNames[gift.targetPlan], formatMoney(coupon.currency, coupon.firstMonth, locale), renewal);
	const dialogAria = coupon === null ? offerLadder.limitWall.regularDialogAria
		: coupon.intro === undefined ? offerLadder.gift.dialogAria : offerLadder.gift.introDialogAria;
	useEffect(() => { if (!open) setAcknowledged(false); }, [open, setAcknowledged]);
	return <ClaimDialog $regular={coupon === null} open={open} maxWidth={false} slots={{transition: SheetTransition}}
		onClose={(_event: object, reason: string) => {
			if (sheetUp && reason === "backdropClick") return;
			onClose();
		}}
		slotProps={{transition: {onExited}, paper: {"aria-label": dialogAria}}}>
		<CloseButton type="button" onClick={onClose} aria-label={common.close}>×</CloseButton>
		<ClaimLayout $regular={coupon === null}>
			{coupon !== null && <TicketColumn><CouponTicket gift={coupon} countdown={countdown} discountsLeft={discountsLeft} stub={false} eyebrow={eyebrow} endsOn={endsOn}/></TicketColumn>}
			<ClaimContent>
				{sheetUp ? (
					<>
						<Continue type="button" data-testid="coupon-claim-checkout-close" onClick={dismissSheet}>
							{common.cancel}
						</Continue>
						<div data-testid="coupon-claim-checkout">
							<SubscriptionElementsCheckout
								plan={gift.targetPlan} clientSecret={clientSecret} collectsShippingAddress={collectsShippingAddress}
								contractDocument={contract.document} awaitingDocument={contract.awaitingDocument}
								consented={acknowledged} buyerEmail={user.email === "" ? undefined : user.email}
								onConsentChange={checked => { setAcknowledged(checked); if (!checked) dismissSheet(); }}
								monthlyPrice={renewal}
								firstCharge={coupon === null || coupon.firstMonth <= 0 ? undefined
									: formatMoney(coupon.currency, coupon.firstMonth, locale)}
								introTerms={coupon === null ? undefined : priceLine}
								onPlaced={paid}
							/>
						</div>
					</>
				) : (
					<>
						{coupon !== null && <ClaimEyebrow>{eyebrow ?? (coupon.intro === undefined ? offerLadder.gift.eyebrow : offerLadder.gift.introEyebrow)}</ClaimEyebrow>}
						<Title>{offerLadder.gift.planLabel(pricing.planNames[gift.targetPlan])}</Title>
						{gift.maxHostedUploadBytes !== null && <ClaimBenefit>{offerLadder.gift.benefit(formatBytes(gift.maxHostedUploadBytes, locale, 0))}</ClaimBenefit>}
						<Lede>{priceLine}</Lede>
						{note !== undefined && <OfferUnavailable role="status">{note}</OfferUnavailable>}
						{expired && <OfferUnavailable role="status">{offerLadder.gift.expired}</OfferUnavailable>}
						{previewStatus === "error" && <OfferUnavailable role="status">{errors.loadFailed}</OfferUnavailable>}
						{/* Why the mint failed: there is no redirect to fall back to on the embedded path,
						  * so a pressed Subscribe that could not start has to say so (the first-month rule). */}
						{checkoutError !== null && <OfferUnavailable role="alert">{checkoutCopy.errors[checkoutError]}</OfferUnavailable>}
						{/* Both variants ask for the express request: a new subscription before its sheet is minted, and a
						  * live subscriber's upgrade before the press that charges it (kitecorp/transferify-ui#3457), whose
						  * box is the plan change's own (`useCouponClaim`). Above the Terms line, so that line still runs
						  * straight into the order button. */}
						<ImmediateDeliveryConsent
							contractDocument={contract.document} awaitingDocument={contract.awaitingDocument}
							effect={withdrawalEffectOf("subscription")} checked={acknowledged}
							onChange={setAcknowledged} testId="coupon-claim-consent"/>
						{/* The Terms link goes where the order is placed. A live subscriber's press IS the order (the
						  * plan change is charged at once and no Stripe form follows), so that variant states one
						  * line and the Terms link directly above its button (kitecorp/transferify-ui#3394): the
						  * plan the account is on, VAT and cancellation. The plan, its prices and the amount charged
						  * now are already on screen (the amount on the button itself), so the line repeats none of
						  * them (owner review of kitecorp/transferify-ui#3396). Anyone else orders inside Stripe's
						  * form, whose order block carries the dialog's one Terms link (kitecorp/transferify-ui#3392).
						  * Nothing interactive may follow an order button (see `pay/SubscriptionElementsCheckout`). */}
						{gift.isUpgrade && <ClaimTerms><OrderTermsLine contractDocument={contract.document}
							text={upgradeChargeLine(user, {planNames: pricing.planNames, sentence: offerLadder.gift.upgradeCharge})}/></ClaimTerms>}
						<Continue type="button" disabled={busy || expired || previewStatus !== "ready" || contract.awaitingDocument || paymentAdmission(acknowledged) === "acknowledgementRequired"} onClick={() => subscribe(contract.latestRead)}>
							{/* A subscriber's button names the amount the press charges now, and only once the preview
							  * has answered it: the calculating sentence until then, never a label without a figure. */}
							{!gift.isUpgrade ? offerLadder.gift.subscribe
								: amountDueNow === null ? pricing.changePlan.calculating : pricing.changePlan.confirm(amountDueNow)}
						</Continue>
					</>
				)}
			</ClaimContent>
		</ClaimLayout>
	</ClaimDialog>;
}
